/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  KnownCustomerNumberMutationResponseOutput,
  KnownCustomerNumberPageResponseOutput,
  KnownCustomerNumberRemoveResponseOutput,
} from '../model';
import type {
  KnownCustomerNumberRemoveBody,
  KnownCustomerNumberWriteBody,
  ListCustomerKnownNumbersParams,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListCustomerKnownNumbersUrl = (
  customerId: string,
  params?: ListCustomerKnownNumbersParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/customers/${customerId}/known-numbers?${stringifiedParams}`
    : `/api/v1/customers/${customerId}/known-numbers`;
};

export const listCustomerKnownNumbers = async (
  customerId: string,
  params?: ListCustomerKnownNumbersParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<KnownCustomerNumberPageResponseOutput> => {
  return validatedFetch<KnownCustomerNumberPageResponseOutput>(
    getListCustomerKnownNumbersUrl(customerId, params),
    {
      ...options,
      method: 'GET',

      schema: KnownCustomerNumberPageResponseOutput,
    },
  );
};

export const getListCustomerKnownNumbersQueryKey = (
  customerId: string,
  params?: ListCustomerKnownNumbersParams,
) => {
  return [`/api/v1/customers/${customerId}/known-numbers`, ...(params ? [params] : [])] as const;
};

export const getListCustomerKnownNumbersQueryOptions = <
  TData = Awaited<ReturnType<typeof listCustomerKnownNumbers>>,
  TError = unknown,
>(
  customerId: string,
  params?: ListCustomerKnownNumbersParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomerKnownNumbers>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCustomerKnownNumbersQueryKey(customerId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCustomerKnownNumbers>>> = ({
    signal,
  }) => listCustomerKnownNumbers(customerId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: customerId !== null && customerId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCustomerKnownNumbers>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListCustomerKnownNumbersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCustomerKnownNumbers>>
>;
export type ListCustomerKnownNumbersQueryError = unknown;

export function useListCustomerKnownNumbers<
  TData = Awaited<ReturnType<typeof listCustomerKnownNumbers>>,
  TError = unknown,
>(
  customerId: string,
  params?: ListCustomerKnownNumbersParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomerKnownNumbers>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCustomerKnownNumbersQueryOptions(customerId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getAddCustomerKnownNumberUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}/known-numbers`;
};

export const addCustomerKnownNumber = async (
  customerId: string,
  knownCustomerNumberWriteBody: KnownCustomerNumberWriteBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<KnownCustomerNumberMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<KnownCustomerNumberMutationResponseOutput>(
    getAddCustomerKnownNumberUrl(customerId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(knownCustomerNumberWriteBody),
      schema: KnownCustomerNumberMutationResponseOutput,
    },
  );
};

export const getAddCustomerKnownNumberMutationKey = () => ['addCustomerKnownNumber'] as const;

export const getAddCustomerKnownNumberMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addCustomerKnownNumber>>,
    TError,
    AddCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addCustomerKnownNumber>>,
  TError,
  AddCustomerKnownNumberMutationVariables,
  TContext
> => {
  const mutationKey = getAddCustomerKnownNumberMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addCustomerKnownNumber>>,
    AddCustomerKnownNumberMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return addCustomerKnownNumber(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddCustomerKnownNumberMutationResult = NonNullable<
  Awaited<ReturnType<typeof addCustomerKnownNumber>>
>;
export type AddCustomerKnownNumberMutationBody = KnownCustomerNumberWriteBody;
export type AddCustomerKnownNumberMutationError = unknown;
export type AddCustomerKnownNumberMutationVariables = {
  customerId: string;
  data: KnownCustomerNumberWriteBody;
};

export const useAddCustomerKnownNumber = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addCustomerKnownNumber>>,
    TError,
    AddCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof addCustomerKnownNumber>>,
  TError,
  AddCustomerKnownNumberMutationVariables,
  TContext
> => {
  return useMutation(getAddCustomerKnownNumberMutationOptions(options));
};
export const getEditCustomerKnownNumberUrl = (customerId: string, knownNumberId: string) => {
  return `/api/v1/customers/${customerId}/known-numbers/${knownNumberId}`;
};

export const editCustomerKnownNumber = async (
  customerId: string,
  knownNumberId: string,
  knownCustomerNumberWriteBody: KnownCustomerNumberWriteBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<KnownCustomerNumberMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<KnownCustomerNumberMutationResponseOutput>(
    getEditCustomerKnownNumberUrl(customerId, knownNumberId),
    {
      ...options,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(knownCustomerNumberWriteBody),
      schema: KnownCustomerNumberMutationResponseOutput,
    },
  );
};

export const getEditCustomerKnownNumberMutationKey = () => ['editCustomerKnownNumber'] as const;

export const getEditCustomerKnownNumberMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof editCustomerKnownNumber>>,
    TError,
    EditCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof editCustomerKnownNumber>>,
  TError,
  EditCustomerKnownNumberMutationVariables,
  TContext
> => {
  const mutationKey = getEditCustomerKnownNumberMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof editCustomerKnownNumber>>,
    EditCustomerKnownNumberMutationVariables
  > = (props) => {
    const { customerId, knownNumberId, data } = props ?? {};

    return editCustomerKnownNumber(customerId, knownNumberId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type EditCustomerKnownNumberMutationResult = NonNullable<
  Awaited<ReturnType<typeof editCustomerKnownNumber>>
>;
export type EditCustomerKnownNumberMutationBody = KnownCustomerNumberWriteBody;
export type EditCustomerKnownNumberMutationError = unknown;
export type EditCustomerKnownNumberMutationVariables = {
  customerId: string;
  knownNumberId: string;
  data: KnownCustomerNumberWriteBody;
};

export const useEditCustomerKnownNumber = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof editCustomerKnownNumber>>,
    TError,
    EditCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof editCustomerKnownNumber>>,
  TError,
  EditCustomerKnownNumberMutationVariables,
  TContext
> => {
  return useMutation(getEditCustomerKnownNumberMutationOptions(options));
};
export const getRemoveCustomerKnownNumberUrl = (customerId: string, knownNumberId: string) => {
  return `/api/v1/customers/${customerId}/known-numbers/${knownNumberId}`;
};

export const removeCustomerKnownNumber = async (
  customerId: string,
  knownNumberId: string,
  knownCustomerNumberRemoveBody: KnownCustomerNumberRemoveBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<KnownCustomerNumberRemoveResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<KnownCustomerNumberRemoveResponseOutput>(
    getRemoveCustomerKnownNumberUrl(customerId, knownNumberId),
    {
      ...options,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(knownCustomerNumberRemoveBody),
      schema: KnownCustomerNumberRemoveResponseOutput,
    },
  );
};

export const getRemoveCustomerKnownNumberMutationKey = () => ['removeCustomerKnownNumber'] as const;

export const getRemoveCustomerKnownNumberMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeCustomerKnownNumber>>,
    TError,
    RemoveCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeCustomerKnownNumber>>,
  TError,
  RemoveCustomerKnownNumberMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveCustomerKnownNumberMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeCustomerKnownNumber>>,
    RemoveCustomerKnownNumberMutationVariables
  > = (props) => {
    const { customerId, knownNumberId, data } = props ?? {};

    return removeCustomerKnownNumber(customerId, knownNumberId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveCustomerKnownNumberMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeCustomerKnownNumber>>
>;
export type RemoveCustomerKnownNumberMutationBody = KnownCustomerNumberRemoveBody;
export type RemoveCustomerKnownNumberMutationError = unknown;
export type RemoveCustomerKnownNumberMutationVariables = {
  customerId: string;
  knownNumberId: string;
  data: KnownCustomerNumberRemoveBody;
};

export const useRemoveCustomerKnownNumber = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeCustomerKnownNumber>>,
    TError,
    RemoveCustomerKnownNumberMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeCustomerKnownNumber>>,
  TError,
  RemoveCustomerKnownNumberMutationVariables,
  TContext
> => {
  return useMutation(getRemoveCustomerKnownNumberMutationOptions(options));
};
